import { useMemo } from 'react';
import { useLocation } from 'react-router';
import _ from 'lodash';

import { useFarmName, useIsFieldsPage } from '../../utils/hooks';

import DraggableModal from '../basics/DraggableModal';
import FarmSummary from '../right-menu/FarmSummary';
import {
  useDesignInfoModalContext,
  useDesignInfoModalTitle,
} from '../../contexts/DesignInfoModalContext';
import { aggregateCropSections } from '../../utils/meta';
import useFarmOrSectionGroupData from '../../hooks/useFarmOrSectionGroupData';

const QUERY_NAME = 'DesignInfoModalQuery';
const FRAGMENT_NAME = 'DesignInfoModalFragment';
const QUERY = `
fragment ${FRAGMENT_NAME} on SectionsAggregate {
  sections {
    cropSections {
      sectionId
      cropId
      treeCount
      rowCount
      totalRowLength
      acreage
      plantedAcreage
      startYearIndex
      cropSectionVarieties {
        cropVarietyId
        treeCount
      }
    }
  }

  crops {
    id
    name
    color {
      id
      hexDark
      hexLight
      name
    }
    cropVarieties {
      id
      name
    }
  }
}
`;

const useDesignData = () => {
  const isFieldsPage = useIsFieldsPage();

  // Fetch the data (except on the Fields page)
  const { data } = useFarmOrSectionGroupData(
    QUERY,
    QUERY_NAME,
    FRAGMENT_NAME,
    [], // parameters
    { skip: isFieldsPage }, // query options
  );

  // Aggregate the data:
  return useMemo(() => {
    const cropSections =
      data?.sections?.map((s) => s.cropSections).flat() ?? [];
    const crops = data?.crops ?? [];
    const cropsById = _.keyBy(crops, 'id');

    const { totals, bySection, cropData } = aggregateCropSections({
      cropSections,
      cropsById,
    });

    // Count the sections that have at least one crop:
    const sectionCount = Object.values(bySection).filter((obj) =>
      Object.values(obj).some((val) => val !== 0),
    ).length;

    return {
      sectionCount,
      totals,
      cropData,
    };
  }, [data]);
};

const ModalContent = () => {
  const { sectionCount, totals, cropData } = useDesignData();

  const farmName = useFarmName();

  return (
    <div className="p-1">
      <FarmSummary
        sectionCount={sectionCount}
        totals={totals}
        cropData={cropData}
        farmName={farmName}
        readonly
      />
    </div>
  );
};

export const DesignInfoModal = () => {
  const { isOpen, setIsOpen } = useDesignInfoModalContext();

  const modalTitle = useDesignInfoModalTitle();

  // Remount ModalContent (and thus, refetch) when page changes:
  const { pathname } = useLocation();

  /**
   * NOTE: this component is a child of <FarmLayout />, and we want to include
   * the design info modal on all of its pages *except* the Fields page.
   *
   * So, we override isOpen if we're on the Fields page.
   */
  const isFieldsPage = useIsFieldsPage();

  return (
    <DraggableModal
      title={modalTitle}
      size="sm"
      isOpen={isOpen && !isFieldsPage}
      setIsOpen={setIsOpen}
    >
      <ModalContent key={pathname} />
    </DraggableModal>
  );
};
